import { useMemo, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  useAddPtoMissingRowMutation,
  useDisputePtoVolumeMutation,
  useFixPtoVolumeMutation,
  useGetPtoVolumeEditOptionsQuery,
  usePerUnitPtoVolumeMutation,
  useRevertPtoVolumeEditMutation,
  useSplitPtoVolumeMutation,
  useWithdrawPtoVolumeEditMutation,
  type PtoEditValues,
  type PtoVolumeEditOptions,
  type PtoVolumeEditOutcome,
  type PtoVolumePosition,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { Modal } from '@shared/ui/Modal';
import { rowLabel, sourcePlace } from '../volumes/rowRef';
import { HistoryView } from './HistoryView';
import { ResolveEditButtons } from './RevisionEditsPanel';
import {
  changesText,
  fixErrors,
  formValues,
  ISSUE_LABELS,
  isChangeDocRow,
  parseNumber,
  perUnitErrors,
  qtyText,
  sheetFormOf,
  sheetRefOf,
  splitErrors,
  textCheckNote,
  valueForm,
  valuesOf,
  valuesText,
  disputeErrors,
  type SheetForm,
  type ValueForm,
} from './logic';
import type { RdVolumeEditAccess } from './useRdVolumeEditAccess';
import {
  Buttons,
  CommentField,
  Done,
  ErrorText,
  Errors,
  FieldLabel,
  Form,
  Grid,
  Muted,
  ReasonSelect,
  Section,
  SheetFields,
  ValueFields,
  Warn,
} from './ui';

/** Действие окна правки строки. */
export type RowEditAction =
  'fix' | 'split' | 'per_unit' | 'missing' | 'dispute' | 'history' | 'revert' | 'withdraw';

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin: ${({ theme }) => theme.spacing(3)} 0;
`;

const Tab = styled.button<{ $active: boolean }>`
  font: inherit;
  font-size: 12.5px;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  padding: 5px 10px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) => ($active ? theme.colors.primarySoft : theme.colors.surface)};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.text.primary)};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Strong = styled.span`
  font-weight: 600;
`;

const PartBox = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(2.5)};
`;

const PartHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
  font-size: 12.5px;
  font-weight: 600;
`;

/** Текст ячеек строки, как их прочла программа. */
function cellsText(cells: unknown): string {
  return Array.isArray(cells)
    ? cells.filter((c): c is string => typeof c === 'string' && c.trim().length > 0).join(' | ')
    : '';
}

/** Второе прочтение строки скана по изображению. */
function visionText(vision: unknown): string {
  if (!vision || typeof vision !== 'object') return '';
  const v = vision as Record<string, unknown>;
  const read = [v.qty, v.unit].filter((x) => typeof x === 'string' && x).join(' ');
  return read ? `${read}${typeof v.verdict === 'string' ? ` (${v.verdict})` : ''}` : '';
}

interface PaneProps {
  position: PtoVolumePosition;
  options: PtoVolumeEditOptions;
  rights: RdVolumeEditAccess;
  onDone: (message: string) => void;
  /** Действие сохранено — позиции и правки изменились (окно листа РД сообщает
   *  экрану «Позиции объёма», что пора перезапросить строки). */
  onChanged: () => void;
  /** Лист и строка по умолчанию — от окна листа РД (№9): страница, лист, строка. */
  sheetHint?: Partial<SheetForm>;
}

/** Итог действия коротко. */
function outcomeText(out: PtoVolumeEditOutcome, what: string): string {
  const parts = [what];
  if (out.added.length > 0) {
    parts.push(
      `добавлено строк: ${out.added.length} (${out.added
        .map((p) => rowLabel(p) ?? `«${p.name}»`)
        .join(', ')})`,
    );
  }
  if (out.pending.length > 0) {
    parts.push(
      `ждут подтверждения другим пользователем с правом правки: ${out.pending.length} — строк нет в тексте листа`,
    );
  }
  return `${parts.join('; ')}.`;
}

// ── «Исправить» ──

function FixPane({ position: p, options, rights, onDone, onChanged, sheetHint }: PaneProps) {
  const [fix, { isLoading }] = useFixPtoVolumeMutation();
  const live = options.live_edit?.kind === 'fix' ? options.live_edit : null;
  const current = valuesOf(p);
  const [value, setValue] = useState<ValueForm>(() => valueForm(current));
  const [sheet, setSheet] = useState<SheetForm>(() => ({ ...sheetFormOf(p), ...sheetHint }));
  const [reason, setReason] = useState(live?.reason_code ?? '');
  const [comment, setComment] = useState('');
  const [shown, setShown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasons = rights.access?.fix_reasons ?? [];
  const errors = fixErrors({
    value,
    sheet,
    reasonCode: reason,
    comment,
    reasons,
    current,
    machine: live ? options.machine : null,
    totalPages: options.total_pages,
    kspLocked: !!options.ksp_binding,
  });
  const preview = formValues(value).value;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setShown(true);
    setError(null);
    if (errors.length > 0) return;
    try {
      await fix({
        id: p.id,
        body: {
          value: preview,
          sheet_ref: sheetRefOf(sheet, options.total_pages, true).sheetRef,
          reason_code: reason,
          comment: comment.trim() || null,
        },
      }).unwrap();
      onChanged();
      onDone(`Исправлено: ${changesText(options.machine, preview) || 'значение строки'}.`);
    } catch (err) {
      setError(apiErrorText(err, 'Правка не сохранена.'));
    }
  }

  return (
    <Form onSubmit={(e) => void submit(e)}>
      <Muted>
        Значение строки по листу РД. Прочтение программы сохранится рядом и в журнале; в ведомости,
        снабжении, у Прораба и в ППР будет одно значение — исправленное.
      </Muted>
      <ValueFields
        value={value}
        onChange={setValue}
        lockQtyUnit={!!options.ksp_binding}
        idPrefix="rd-fix"
      />
      <SheetFields
        sheet={sheet}
        onChange={setSheet}
        totalPages={options.total_pages}
        idPrefix="rd-fix"
      />
      <Grid $cols={2}>
        <ReasonSelect value={reason} onChange={setReason} reasons={reasons} id="rd-fix-reason" />
      </Grid>
      <CommentField value={comment} onChange={setComment} id="rd-fix-comment" />
      {changesText(options.machine, preview) && (
        <Section>
          <Strong>Было → стало:</Strong> {changesText(options.machine, preview)}
        </Section>
      )}
      {shown && <Errors list={errors} />}
      {error && <ErrorText>{error}</ErrorText>}
      <Buttons>
        <Button
          type="submit"
          $variant="primary"
          disabled={isLoading || (shown && errors.length > 0)}
        >
          {isLoading ? 'Сохранение…' : live ? 'Изменить правку' : 'Исправить'}
        </Button>
      </Buttons>
    </Form>
  );
}

// ── «Разделить строку» ──

function SplitPane({ position: p, options, onDone, onChanged, sheetHint }: PaneProps) {
  const [split, { isLoading }] = useSplitPtoVolumeMutation();
  const [parts, setParts] = useState<ValueForm[]>(() => [
    valueForm(valuesOf(p)),
    valueForm({ name: '', unit: p.unit }),
  ]);
  const [sheet, setSheet] = useState<SheetForm>(() => ({ ...sheetFormOf(p), ...sheetHint }));
  const [comment, setComment] = useState('');
  const [shown, setShown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errors = splitErrors(parts, sheet, options.total_pages);
  const kspLockedFirst = !!options.ksp_binding;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setShown(true);
    setError(null);
    if (errors.length > 0) return;
    try {
      const out = await split({
        id: p.id,
        body: {
          parts: parts.map((x) => formValues(x).value),
          sheet_ref: sheetRefOf(sheet, options.total_pages, false).sheetRef,
          comment: comment.trim() || null,
        },
      }).unwrap();
      onChanged();
      onDone(outcomeText(out, `Строка разделена на ${parts.length} ч.: первая — исправлена`));
    } catch (err) {
      setError(apiErrorText(err, 'Строка не разделена.'));
    }
  }

  const setPart = (i: number) => (next: ValueForm) =>
    setParts((prev) => prev.map((x, k) => (k === i ? next : x)));

  return (
    <Form onSubmit={(e) => void submit(e)}>
      <Muted>
        Склеенная строка исправляется на первую часть, остальные добавляются после неё как
        пропущенные строки — каждая проверяется по тексту листа; не найденная ждёт подтверждения
        другим пользователем с правом правки. Одна запись журнала.
      </Muted>
      {parts.map((part, i) => (
        <PartBox key={i}>
          <PartHead>
            <span>{i === 0 ? 'Часть 1 — сама строка' : `Часть ${i + 1} — новая строка`}</span>
            {i >= 2 && (
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                onClick={() => setParts((prev) => prev.filter((_, k) => k !== i))}
              >
                Убрать
              </Button>
            )}
          </PartHead>
          <ValueFields
            value={part}
            onChange={setPart(i)}
            compact
            lockQtyUnit={i === 0 && kspLockedFirst}
            idPrefix={`rd-split-${i}`}
          />
        </PartBox>
      ))}
      {parts.length < 50 && (
        <div>
          <Button
            type="button"
            $variant="secondary"
            $size="sm"
            onClick={() => setParts((prev) => [...prev, valueForm({ name: '', unit: p.unit })])}
          >
            + Часть
          </Button>
        </div>
      )}
      <SheetFields
        sheet={sheet}
        onChange={setSheet}
        totalPages={options.total_pages}
        idPrefix="rd-split"
      />
      <CommentField value={comment} onChange={setComment} id="rd-split-comment" />
      {shown && <Errors list={errors} />}
      {error && <ErrorText>{error}</ErrorText>}
      <Buttons>
        <Button
          type="submit"
          $variant="primary"
          disabled={isLoading || (shown && errors.length > 0)}
        >
          {isLoading ? 'Сохранение…' : 'Разделить строку'}
        </Button>
      </Buttons>
    </Form>
  );
}

// ── Кратность «на 1 …» ──

function PerUnitPane({ position: p, options, onDone, onChanged, sheetHint }: PaneProps) {
  const [perUnit, { isLoading }] = usePerUnitPtoVolumeMutation();
  const live = options.live_edit?.kind === 'per_unit' ? options.live_edit : null;
  const [count, setCount] = useState(
    live?.per_unit_multiplier ? String(live.per_unit_multiplier) : '',
  );
  const [sheet, setSheet] = useState<SheetForm>(() => ({ ...sheetFormOf(p), ...sheetHint }));
  const [comment, setComment] = useState('');
  const [shown, setShown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errors = perUnitErrors(count, sheet, options.total_pages);
  const unitQty = options.machine.quantity ?? null;
  const n = parseNumber(count);
  const total =
    typeof n === 'number' && n > 0 && unitQty !== null ? Math.round(unitQty * n * 1e6) / 1e6 : null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setShown(true);
    setError(null);
    if (errors.length > 0 || typeof n !== 'number') return;
    try {
      await perUnit({
        id: p.id,
        body: {
          count: n,
          sheet_ref: sheetRefOf(sheet, options.total_pages, true).sheetRef,
          comment: comment.trim() || null,
        },
      }).unwrap();
      onChanged();
      onDone(
        `Кратность указана: на 1 — ${qtyText(unitQty, options.machine.unit)} × ${qtyText(n)}.`,
      );
    } catch (err) {
      setError(apiErrorText(err, 'Кратность не сохранена.'));
    }
  }

  return (
    <Form onSubmit={(e) => void submit(e)}>
      <Muted>
        Явное число элементов из РД и лист со строкой, где оно написано. Количество = на 1 × число —
        считает программа.
      </Muted>
      <Section>
        На 1 элемент — <Strong>{qtyText(unitQty, options.machine.unit)}</Strong>
        {total !== null && (
          <>
            {' '}
            · будет <Strong>{qtyText(total, options.machine.unit)}</Strong>
          </>
        )}
      </Section>
      <Grid $cols={3}>
        <FieldLabel htmlFor="rd-per-unit-count">
          Число элементов
          <Input
            id="rd-per-unit-count"
            inputMode="decimal"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            autoFocus
          />
        </FieldLabel>
      </Grid>
      <SheetFields
        sheet={sheet}
        onChange={setSheet}
        totalPages={options.total_pages}
        rowLabel="Где написано число (строка, примечание)"
        idPrefix="rd-per-unit"
      />
      <CommentField value={comment} onChange={setComment} id="rd-per-unit-comment" />
      {shown && <Errors list={errors} />}
      {error && <ErrorText>{error}</ErrorText>}
      <Buttons>
        <Button
          type="submit"
          $variant="primary"
          disabled={isLoading || (shown && errors.length > 0)}
        >
          {isLoading ? 'Сохранение…' : 'Указать кратность'}
        </Button>
      </Buttons>
    </Form>
  );
}

// ── «Строка не распознана программой» ──

function MissingRowPane({ position: p, options, onDone, onChanged }: PaneProps) {
  const [addRow, { isLoading }] = useAddPtoMissingRowMutation();
  const [value, setValue] = useState<ValueForm>(() => valueForm({ name: '', unit: p.unit }));
  const [sheet, setSheet] = useState<SheetForm>(() => ({ ...sheetFormOf(p), row: '' }));
  const [comment, setComment] = useState('');
  const [shown, setShown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PtoVolumeEditOutcome | null>(null);
  const valueErrors = formValues(value).errors;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setShown(true);
    setError(null);
    if (valueErrors.length > 0) return;
    try {
      const out = await addRow({
        id: p.id,
        body: {
          value: formValues(value).value,
          sheet_ref: sheetRefOf(sheet, options.total_pages, false).sheetRef,
          comment: comment.trim() || null,
        },
      }).unwrap();
      setResult(out);
      onChanged();
    } catch (err) {
      setError(apiErrorText(err, 'Строка не внесена.'));
    }
  }

  if (result) {
    const added = result.added[0];
    return (
      <Form onSubmit={(e) => e.preventDefault()}>
        {added ? (
          <Done role="status">
            {textCheckNote(result.text_check)} Номер — {rowLabel(added) ?? 'будет присвоен'}: строка
            встала после «{p.name}».
          </Done>
        ) : (
          <Warn role="status">
            {textCheckNote(result.text_check)} Её подтверждает другой «Инженер+» в блоке «Правки
            ревизии, которые ждут решения» над таблицей.
          </Warn>
        )}
        <Buttons>
          <Button type="button" $variant="primary" onClick={() => onDone('')}>
            Готово
          </Button>
        </Buttons>
      </Form>
    );
  }

  return (
    <Form onSubmit={(e) => void submit(e)}>
      <Muted>
        Строка листа, которую программа пропустила. Она встанет после этой строки и унаследует от
        таблицы тип, категорию, часть и систему; номер — «N.k». Программа проверит её по тексту
        листа: не найдёт — строку подтверждает другой пользователь с правом правки.
      </Muted>
      <ValueFields value={value} onChange={setValue} idPrefix="rd-missing" />
      <SheetFields
        sheet={sheet}
        onChange={setSheet}
        totalPages={options.total_pages}
        pageLocked
        rowLabel="Строка на листе (необязательно)"
        idPrefix="rd-missing"
      />
      <CommentField value={comment} onChange={setComment} id="rd-missing-comment" />
      {shown && <Errors list={valueErrors} />}
      {error && <ErrorText>{error}</ErrorText>}
      <Buttons>
        <Button
          type="submit"
          $variant="primary"
          disabled={isLoading || (shown && valueErrors.length > 0)}
        >
          {isLoading ? 'Проверка по листу…' : 'Внести строку'}
        </Button>
      </Buttons>
    </Form>
  );
}

// ── «Спорно» ──

function DisputePane({
  position: p,
  options,
  rights,
  onDone,
  onChanged,
  sheetHint,
}: PaneProps) {
  const [dispute, { isLoading }] = useDisputePtoVolumeMutation();
  const [sheet, setSheet] = useState<SheetForm>(() => ({ ...sheetFormOf(p), ...sheetHint }));
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [shown, setShown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasons = rights.access?.dispute_reasons ?? [];
  const errors = disputeErrors({
    sheet,
    reasonCode: reason,
    comment,
    reasons,
    totalPages: options.total_pages,
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setShown(true);
    setError(null);
    if (errors.length > 0) return;
    try {
      await dispute({
        id: p.id,
        body: {
          sheet_ref: sheetRefOf(sheet, options.total_pages, false).sheetRef,
          reason_code: reason,
          comment: comment.trim() || null,
        },
      }).unwrap();
      onChanged();
      onDone('Отмечено «Спорно» — запись в журнале правок.');
    } catch (err) {
      setError(apiErrorText(err, 'Отметка «Спорно» не сохранена.'));
    }
  }

  return (
    <Form onSubmit={(e) => void submit(e)}>
      <Muted>
        Несогласие без правки: значение строки не меняется, отметка с листом и причиной уходит в
        журнал. Изменение проекта — только по документу изменения, согласованному Заказчиком.
      </Muted>
      <SheetFields
        sheet={sheet}
        onChange={setSheet}
        totalPages={options.total_pages}
        rowLabel="Строка на листе (необязательно)"
        idPrefix="rd-dispute"
      />
      <Grid $cols={2}>
        <ReasonSelect
          value={reason}
          onChange={setReason}
          reasons={reasons}
          id="rd-dispute-reason"
        />
      </Grid>
      <CommentField value={comment} onChange={setComment} id="rd-dispute-comment" />
      {shown && <Errors list={errors} />}
      {error && <ErrorText>{error}</ErrorText>}
      <Buttons>
        <Button
          type="submit"
          $variant="primary"
          disabled={isLoading || (shown && errors.length > 0)}
        >
          {isLoading ? 'Сохранение…' : 'Отметить «Спорно»'}
        </Button>
      </Buttons>
    </Form>
  );
}

// ── «Отменить» и «Отозвать» ──

function CloseEditPane({
  options,
  mode,
  onDone,
  onChanged,
}: PaneProps & { mode: 'revert' | 'withdraw' }) {
  const [revert, { isLoading: reverting }] = useRevertPtoVolumeEditMutation();
  const [withdraw, { isLoading: withdrawing }] = useWithdrawPtoVolumeEditMutation();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const live = options.live_edit;
  const busy = reverting || withdrawing;
  if (!live) return <Muted>У строки нет действующей правки.</Muted>;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!reason.trim() || !live) return;
    try {
      if (mode === 'withdraw') {
        await withdraw({ id: live.id, reason: reason.trim() }).unwrap();
        onChanged();
        onDone('Позиция отозвана: она вне сумм и остаётся в истории.');
      } else {
        await revert({ id: live.id, reason: reason.trim() }).unwrap();
        onChanged();
        onDone(
          live.kind === 'manual'
            ? 'Ручная строка отменена: она вне сумм и остаётся в истории.'
            : 'Правка отменена: у строки снова прочтение программы.',
        );
      }
    } catch (err) {
      setError(
        apiErrorText(err, mode === 'withdraw' ? 'Позиция не отозвана.' : 'Правка не отменена.'),
      );
    }
  }

  return (
    <Form onSubmit={(e) => void submit(e)}>
      <Warn>
        {mode === 'withdraw'
          ? 'Позицию по документу изменения не удаляют: отозванная, она выходит из сумм и остаётся в истории и журнале.'
          : live.kind === 'manual'
            ? 'Ручная строка не удаляется: отменённая, она выходит из сумм и остаётся в истории.'
            : `Строке вернётся прочтение программы: ${valuesText(options.machine)}. Правка останется в истории.`}
      </Warn>
      <CommentField
        value={reason}
        onChange={setReason}
        id="rd-close-reason"
        label="Причина (обязательно)"
      />
      {error && <ErrorText>{error}</ErrorText>}
      <Buttons>
        <Button type="submit" $variant="primary" disabled={busy || !reason.trim()}>
          {busy ? 'Сохранение…' : mode === 'withdraw' ? 'Отозвать' : 'Отменить правку'}
        </Button>
      </Buttons>
    </Form>
  );
}

// ── Окно ──

interface Props {
  position: PtoVolumePosition;
  rights: RdVolumeEditAccess;
  initial: RowEditAction;
  onClose: () => void;
  /** Лист и строка по умолчанию (окно листа РД, №9). */
  sheetHint?: Partial<SheetForm>;
  /** Действие сохранено (окно листа РД шлёт экрану `pto_volume_changed`). */
  onChanged?: () => void;
}

/** Доступные действия по ответу `edit-options` и правам. */
function actionsOf(
  p: PtoVolumePosition,
  o: PtoVolumeEditOptions | undefined,
  r: RdVolumeEditAccess,
): { key: RowEditAction; label: string; enabled: boolean; title?: string }[] {
  const live = o?.live_edit ?? null;
  const out: { key: RowEditAction; label: string; enabled: boolean; title?: string }[] = [];
  if (r.canEdit && o) {
    if (!isChangeDocRow(p)) {
      out.push({
        key: 'fix',
        label: 'Исправить',
        enabled: o.can_fix,
        title: o.can_fix ? undefined : (o.blocked ?? 'Исправление недоступно'),
      });
      out.push({
        key: 'split',
        label: 'Разделить строку',
        enabled: o.can_split,
        title: o.can_split ? undefined : (o.blocked ?? 'У строки уже есть правка'),
      });
      if (o.can_per_unit || o.issues.includes('per_unit')) {
        out.push({ key: 'per_unit', label: 'Кратность «на 1 …»', enabled: o.can_per_unit });
      }
      out.push({
        key: 'missing',
        label: 'Строка не распознана программой',
        enabled: o.can_add_after,
        title: o.can_add_after
          ? 'Внести пропущенную строку после этой'
          : 'Только после строки таблицы действующей ревизии',
      });
    }
    if (
      live &&
      live.kind === 'change_doc' &&
      ['active', 'not_in_revision', 'needs_decision'].includes(live.status)
    ) {
      out.push({ key: 'withdraw', label: 'Отозвать', enabled: true });
    }
    if (live && live.kind !== 'change_doc' && ['active', 'conflict'].includes(live.status)) {
      out.push({ key: 'revert', label: 'Отменить правку', enabled: true });
    }
  }
  if (r.canDispute) out.push({ key: 'dispute', label: 'Спорно', enabled: true });
  out.push({ key: 'history', label: 'История', enabled: true });
  return out;
}

/** Окно правки строки ведомости объёмов «Инженер+» (№8) на экране «Объёмы»:
 *  панель правки [`RowEditPanel`] в модальном окне. */
export function RowEditDialog(props: Props) {
  const label = rowLabel(props.position);
  return (
    <Modal
      open
      title={label ? `Строка ${label}` : 'Строка ведомости объёмов'}
      onClose={props.onClose}
    >
      <RowEditPanel {...props} />
    </Modal>
  );
}

/** Панель правки строки «Инженер+» (№8): «Исправить», «Разделить строку»,
 *  кратность «на 1 …», «Строка не распознана программой», «Спорно»,
 *  «Отменить» / «Отозвать», история. Что доступно — по `edit-options`
 *  (признаки нераспознанного, привязка КСП) и правам. Без модального окна —
 *  её же встраивает окно листа РД (№9), `sheetHint` — лист и строка из окна. */
export function RowEditPanel({
  position: p,
  rights,
  initial,
  onClose,
  sheetHint,
  onChanged,
}: Props) {
  const {
    data: options,
    isLoading,
    error,
  } = useGetPtoVolumeEditOptionsQuery(p.id, {
    refetchOnMountOrArgChange: true,
  });
  const actions = useMemo(() => actionsOf(p, options, rights), [p, options, rights]);
  const [picked, setPicked] = useState<RowEditAction | null>(null);
  const [done, setDone] = useState<string | null>(null);
  // Запрошенное действие недоступно (строка распознана) — первое доступное:
  // «Спорно» или история.
  const action: RowEditAction =
    picked ??
    actions.find((a) => a.key === initial && a.enabled)?.key ??
    actions.find((a) => a.enabled && a.key === 'dispute')?.key ??
    'history';
  const place = sourcePlace(p);
  const machine: PtoEditValues | undefined = options?.machine;
  const now = valuesOf(p);
  const programDiffers = machine ? changesText(machine, now) : '';
  const cells = cellsText(options?.cells);
  const vision = visionText(options?.vision);
  const requestedBlocked = initial === 'fix' && options && !options.can_fix;
  const changed = () => onChanged?.();
  const pane = { position: p, rights, onDone: setDone, onChanged: changed, sheetHint };

  return (
    <div>
      <Section>
        <div>
          <Strong>{p.name}</Strong> — {qtyText(p.quantity, p.unit)}
        </div>
        {place && <Muted>{place}</Muted>}
        {programDiffers && machine && <div>Прочтение программы: {valuesText(machine)}</div>}
        {cells && <div>Ячейки строки: {cells}</div>}
        {vision && <div>По изображению: {vision}</div>}
        {options && options.issues.length > 0 && (
          <div>Не распознано: {options.issues.map((i) => ISSUE_LABELS[i] ?? i).join(', ')}</div>
        )}
      </Section>
      {isLoading && <Muted style={{ marginTop: 8 }}>Проверяю, что можно сделать со строкой…</Muted>}
      {error && (
        <ErrorText style={{ marginTop: 8 }}>
          {apiErrorText(error, 'Не удалось проверить строку.')}
        </ErrorText>
      )}
      {options?.ksp_binding && (
        <Warn style={{ marginTop: 8 }}>
          Позиция привязана к графику КСП (работа «{options.ksp_binding}»). Пока с сессией КСП не
          решён вопрос снимка позиции, количество и единицу исправить нельзя; наименование и
          обозначение — можно.
        </Warn>
      )}
      {requestedBlocked && options?.blocked && !picked && (
        <Warn style={{ marginTop: 8 }}>«Исправить» недоступно: {options.blocked}</Warn>
      )}
      {options?.live_edit?.status === 'conflict' && rights.canEdit && done === null && (
        <Warn style={{ marginTop: 8 }}>
          <div style={{ marginBottom: 6 }}>
            Конфликт правки после переобработки: программа прочла строку иначе, правка не применена
            (правка — {valuesText(options.live_edit.value)}). Решите, что действует:
          </div>
          <ResolveEditButtons
            edit={options.live_edit}
            onDone={(m) => {
              changed();
              setDone(m);
            }}
          />
        </Warn>
      )}
      {done !== null ? (
        <>
          {done && (
            <Done role="status" style={{ marginTop: 12 }}>
              {done}
            </Done>
          )}
          <Buttons style={{ marginTop: 12 }}>
            <Button type="button" $variant="primary" onClick={onClose}>
              Закрыть
            </Button>
            <Button
              type="button"
              $variant="secondary"
              onClick={() => {
                setDone(null);
                setPicked('history');
              }}
            >
              История позиции
            </Button>
          </Buttons>
        </>
      ) : isLoading ? null : (
        // Пока нет ответа `edit-options`, действий не показываем: иначе вкладка
        // переключится сама, когда ответ придёт.
        <>
          <Tabs role="tablist">
            {actions.map((a) => (
              <Tab
                key={a.key}
                type="button"
                role="tab"
                aria-selected={a.key === action}
                $active={a.key === action}
                disabled={!a.enabled}
                title={a.title}
                onClick={() => setPicked(a.key)}
              >
                {a.label}
              </Tab>
            ))}
          </Tabs>
          {options && action === 'fix' && <FixPane {...pane} options={options} />}
          {options && action === 'split' && <SplitPane {...pane} options={options} />}
          {options && action === 'per_unit' && <PerUnitPane {...pane} options={options} />}
          {options && action === 'missing' && <MissingRowPane {...pane} options={options} />}
          {options && action === 'dispute' && <DisputePane {...pane} options={options} />}
          {options && (action === 'revert' || action === 'withdraw') && (
            <CloseEditPane {...pane} options={options} mode={action} />
          )}
          {action === 'history' && <HistoryView positionId={p.id} access={rights.access} />}
          <Buttons style={{ marginTop: 12, justifyContent: 'flex-end' }}>
            <Button type="button" $variant="ghost" onClick={onClose}>
              Закрыть
            </Button>
          </Buttons>
        </>
      )}
    </div>
  );
}
